import { useSuspenseQuery } from "@tanstack/react-query"
import { MoonStar } from "lucide-react"
import { Suspense, useEffect, useState } from "react"
import { EmptyChildrenState } from "@/features/children/EmptyChildrenState"
import { useSelectedChild } from "@/features/children/useSelectedChild"
import { SessionFormDialog } from "@/features/sleep/SessionFormDialog"
import { PageLoader } from "@/shared/components/PageLoader"
import { formatClock, formatDuration, minutesBetween, sleepElapsedLabel } from "@/shared/utils/time"
import {
  type Dashboard,
  type DashboardSession,
  dashboardQueryOptions,
} from "./api/dashboard.queries"
import { DayArc } from "./DayArc"
import { type ArcWindow, resolveArcWindow, SCORE_TICK_MS } from "./dashboard.config"
import { ScoreDisplay } from "./ScoreDisplay"

export function DashboardPage() {
  const { selectedChild } = useSelectedChild()

  if (!selectedChild) {
    return <EmptyChildrenState />
  }

  // Keyed remount on child switch resets the local clock/score cleanly.
  return (
    <Suspense fallback={<PageLoader />}>
      <DashboardView key={selectedChild.id} childId={selectedChild.id} />
    </Suspense>
  )
}

function DashboardView({ childId }: { childId: string }) {
  const { data: dashboard, dataUpdatedAt } = useSuspenseQuery(dashboardQueryOptions(childId))
  const [now, setNow] = useState(() => new Date())
  const [editSession, setEditSession] = useState<DashboardSession | null>(null)

  // Tick the local clock so score/durations update between refetches.
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), SCORE_TICK_MS)
    return () => clearInterval(timer)
  }, [])

  const nowMs = now.getTime()
  const sleeping = dashboard.currentSession !== null
  const arcWindow = resolveArcWindow(
    now,
    dashboard.predictions.dayStart,
    dashboard.predictions.nightStart,
  )

  // Approximation: while sleeping, +1 min per minute since the last server value;
  // awake → hold. The refetch corrects drift.
  const liveExtraMin = sleeping ? minutesBetween(nowMs, dataUpdatedAt) : 0
  const displayedScore = dashboard.scoreMinutes + liveExtraMin
  const sinceMorning =
    dashboard.sinceMorningWakeMinutes !== null
      ? dashboard.sinceMorningWakeMinutes + liveExtraMin
      : null

  return (
    <div className="flex h-full flex-col gap-6 p-4">
      <DayArc
        dashboard={dashboard}
        now={now}
        window={arcWindow}
        center={<CenterState dashboard={dashboard} nowMs={nowMs} window={arcWindow} />}
        onSelectSession={setEditSession}
      />
      <ScoreDisplay minutes={displayedScore} className="mt-10" />
      {sinceMorning !== null && (
        <p className="text-center text-sm text-muted-foreground">
          {sinceMorning > 0
            ? `Har sovit ${formatDuration(sinceMorning)} sedan i morse`
            : "Har inte sovit sedan i morse"}
        </p>
      )}
      {editSession && (
        <SessionFormDialog
          key={editSession.id}
          session={editSession}
          open
          onOpenChange={(next) => {
            if (!next) setEditSession(null)
          }}
          onSaved={() => setEditSession(null)}
        />
      )}
    </div>
  )
}

// Arc center: current sleep state, or — awake — the predicted wake (night) / next nap (day).
function CenterState({
  dashboard,
  nowMs,
  window,
}: {
  dashboard: Dashboard
  nowMs: number
  window: ArcWindow
}) {
  const { currentSession, predictions, child, openSessionSuspicious } = dashboard

  if (currentSession) {
    const minutesAsleep = minutesBetween(nowMs, new Date(currentSession.startTime).getTime())
    return (
      <div className="flex flex-col items-center gap-1 text-center">
        <MoonStar className="size-10 animate-moon-glow text-accent" />
        <span className="text-xs text-muted-foreground">{sleepElapsedLabel(minutesAsleep)}</span>
        {openSessionSuspicious && (
          <span className="text-[11px] text-muted-foreground">Sover {child.name} fortfarande?</span>
        )}
      </div>
    )
  }

  if (window.mode === "night") {
    return (
      <div className="flex flex-col items-center gap-0.5 text-center">
        <span className="text-base font-semibold">Trolig vakning</span>
        <span className="text-sm text-muted-foreground">
          ca {formatClock(new Date(window.endMs))}
        </span>
      </div>
    )
  }

  const napAt = predictions.nextNapAt ? new Date(predictions.nextNapAt).getTime() : null
  if (napAt !== null && napAt > nowMs) {
    const inMin = Math.round((napAt - nowMs) / 60_000)
    return (
      <div className="flex flex-col items-center gap-0.5 text-center">
        <span className="text-base font-semibold">Trolig vila</span>
        <span className="text-sm text-muted-foreground">om ca {formatDuration(inMin)}</span>
      </div>
    )
  }

  return <span className="text-sm text-muted-foreground">Ingen vila inplanerad</span>
}
